<template>
  <nav v-if="!hideOnSinglePage || pageCount > 1" class="site-pagination" :aria-label="label">
    <NiceButton
      class="pagination-direction"
      type="button"
      variant="outline"
      :disabled="page <= 1 || pending"
      aria-label="上一页"
      @click="goToPage(page - 1)"
    >
      <i class="i-lucide-chevron-left" aria-hidden="true"></i>
    </NiceButton>

    <div class="pagination-pages">
      <template v-for="token in paginationItems" :key="token">
        <span v-if="typeof token === 'string'" class="pagination-ellipsis" aria-hidden="true">
          ···
        </span>
        <NiceButton
          v-else
          class="pagination-page"
          :class="{ active: token === page }"
          type="button"
          variant="outline"
          :disabled="pending"
          :aria-label="`第 ${token} 页`"
          :aria-current="token === page ? 'page' : undefined"
          @click="goToPage(token)"
        >
          {{ token }}
        </NiceButton>
      </template>
    </div>

    <NiceButton
      class="pagination-direction"
      type="button"
      variant="outline"
      :disabled="page >= pageCount || pending"
      aria-label="下一页"
      @click="goToPage(page + 1)"
    >
      <i class="i-lucide-chevron-right" aria-hidden="true"></i>
    </NiceButton>
  </nav>
</template>

<script setup lang="ts">
  import { NiceButton } from '@nice/nice-ui'

  type PaginationToken = 'end-ellipsis' | 'start-ellipsis' | number

  interface Props {
    hideOnSinglePage?: boolean
    label?: string
    page: number
    pageCount: number
    pending?: boolean
  }

  defineOptions({
    name: 'SitePagination',
  })

  const props = withDefaults(defineProps<Props>(), {
    hideOnSinglePage: false,
    label: '内容分页',
    pending: false,
  })

  const emit = defineEmits<{
    'update:page': [page: number]
  }>()

  const paginationItems = computed<PaginationToken[]>(() => {
    if (props.pageCount <= 7) {
      return Array.from({ length: props.pageCount }, (_, index) => index + 1)
    }

    const pages: PaginationToken[] = [1]
    const start = Math.max(2, props.page - 1)
    const end = Math.min(props.pageCount - 1, props.page + 1)

    if (start > 2) pages.push('start-ellipsis')
    for (let current = start; current <= end; current += 1) pages.push(current)
    if (end < props.pageCount - 1) pages.push('end-ellipsis')
    pages.push(props.pageCount)
    return pages
  })

  /** 请求有效范围内的页码。 */
  function goToPage(nextPage: number) {
    if (props.pending || nextPage === props.page || nextPage < 1 || nextPage > props.pageCount)
      return
    emit('update:page', nextPage)
  }
</script>

<style scoped lang="scss">
  .site-pagination {
    display: flex;
    flex-wrap: wrap;
    gap: 6px;
    align-items: center;
    justify-content: center;
    margin-top: 32px;
  }

  .pagination-pages {
    display: flex;
    gap: 5px;
    align-items: center;
  }

  .site-pagination button {
    display: inline-flex;
    gap: 6px;
    align-items: center;
    justify-content: center;
    min-width: 32px;
    height: 32px;
    padding: 0 8px;
    color: var(--ink);
    cursor: pointer;
    background: var(--surface);
    border: 1px solid var(--line);
    border-radius: var(--radius-md);
    transition:
      color var(--fast) var(--ease),
      background var(--fast) var(--ease),
      border-color var(--fast) var(--ease);
  }

  .site-pagination .pagination-page.active {
    color: var(--white);
    background: var(--accent);
    border-color: var(--accent);
  }

  .site-pagination button:disabled {
    cursor: not-allowed;
    opacity: 0.48;
  }

  .pagination-direction i {
    width: 14px;
    height: 14px;
  }

  .site-pagination .pagination-direction {
    width: 32px;
    padding: 0;
  }

  .pagination-ellipsis {
    display: grid;
    place-items: center;
    min-width: 22px;
    color: var(--muted);
  }

  @media (hover: hover) and (pointer: fine) {
    .site-pagination button:hover:not(:disabled) {
      color: var(--white);
      background: var(--accent);
      border-color: var(--accent);
    }
  }

  @media (hover: none) {
    .site-pagination button:active:not(:disabled) {
      color: var(--white);
      background: var(--accent);
      border-color: var(--accent);
    }
  }

  @media (max-width: 767px) {
    .site-pagination {
      flex-wrap: nowrap;
      gap: 6px;
      justify-content: flex-start;
      width: 100%;
      padding-bottom: 4px;
      margin-top: 24px;
      overflow-x: auto;
    }

    .pagination-direction:first-child {
      margin-left: auto;
    }

    .pagination-direction:last-child {
      margin-right: auto;
    }

    .pagination-pages {
      flex: 0 0 auto;
      gap: 5px;
    }

    .site-pagination button {
      flex: 0 0 auto;
      min-width: 32px;
      height: 32px;
      padding-inline: 8px;
    }

    .pagination-direction i {
      width: 14px;
      height: 14px;
    }

    .site-pagination .pagination-direction {
      width: 32px;
    }
  }
</style>
